import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

export interface PageJumpLink {
  id: string;
  num: string;
  ar: string;
  en: string;
}

interface PageJumpNavProps {
  isRTL: boolean;
  links: PageJumpLink[];
  label?: { ar: string; en: string };
  className?: string;
}

export function PageJumpNav({ isRTL, links, className }: PageJumpNavProps) {
  const [activeId, setActiveId] = useState<string>(links[0]?.id ?? "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0 && visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -60% 0px", threshold: 0 },
    );

    links.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [links]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
  };

  return (
    <div
      className={cn(
        "sticky top-0 z-30 w-full bg-card/90 backdrop-blur-md border-b border-border",
        className,
      )}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-11 flex items-stretch gap-0 overflow-x-auto scrollbar-none">
        {links.map((link) => {
          const active = activeId === link.id;
          return (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(e) => handleClick(e, link.id)}
              className={cn(
                "shrink-0 inline-flex items-center px-4 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap",
                active
                  ? "border-primary text-primary"
                  : "border-transparent text-foreground-secondary hover:text-foreground hover:border-border",
              )}
            >
              {isRTL ? link.ar : link.en}
            </a>
          );
        })}
      </div>
    </div>
  );
}
